import React from "react";
import { useMailContext } from "../../context/MailContext";
import { canReadMailbox, canSendMailbox } from "../../utils/mailboxAccess";
import { I } from "../redesign/kvpIcons";
import { normalisiereStandardAbsender } from "./startAbsender";
import { useGlockenPostfaecher, useStandardAbsender } from "./useMailPostfachEinstellungen";
import "./mailSettings.css";

const klein = (adresse: string | null | undefined): string => (adresse ?? "").trim().toLowerCase();

/**
 * Reiter „Postfächer" im Mail-Einstellungsfenster: zwei Einstellungen, die
 * festlegen, wie die Person mit mehreren Postfächern arbeitet.
 *
 *  - T-249: Welche Postfächer zählen in der Glocke (auch Badge „Schriftverkehr"
 *    und Mitteilungszentrum)? Standard: nur das eigene — dann stimmt die Zahl
 *    mit der am eigenen Posteingang überein.
 *  - T-289: Standard-Absender für neue Mails und Antworten aus dem eigenen
 *    Postfach (Regel: `startAbsender`).
 *
 * Beide Werte liegen pro Person auf dem Server und wirken sofort in der
 * offenen Anwendung (gemeinsamer Stand, siehe `nutzerEinstellung`).
 */
export const MailPostfachSection: React.FC = () => {
    const { mailboxes, ownMailbox, unreadByMailbox } = useMailContext();
    const glocke = useGlockenPostfaecher();
    const absender = useStandardAbsender();

    const lesbare = mailboxes.filter(canReadMailbox);
    const sendbare = mailboxes.filter(canSendMailbox);

    // Im Standard (`null`) zählt das eigene Postfach — die Häkchen zeigen das an.
    const gewaehlt = glocke.wert ?? (ownMailbox ? [klein(ownMailbox)] : []);
    const umschalten = (adresse: string) => {
        const a = klein(adresse);
        glocke.setze(gewaehlt.includes(a) ? gewaehlt.filter((x) => x !== a) : [...gewaehlt, a]);
    };
    const ungelesen = (adresse: string): number => {
        const treffer = Object.entries(unreadByMailbox).find(([mb]) => klein(mb) === klein(adresse));
        return treffer?.[1] ?? 0;
    };

    // Die `<option>`-Werte tragen die Schreibweise der Postfachliste.
    const absenderWert = absender.wert
        ? sendbare.find((m) => klein(m.mailboxAddress) === absender.wert)?.mailboxAddress ?? absender.wert
        : "";
    const absenderOhneSenderecht = !!absender.wert && !sendbare.some((m) => klein(m.mailboxAddress) === absender.wert);

    return (
        <>
            <section className="mset mset-card" role="group" aria-labelledby="mset-glocke-titel">
                <h2 className="mset-title" id="mset-glocke-titel">
                    Welche Postfächer zählen in der Glocke?
                </h2>
                <div className="mset-hint">
                    Die Zahl der ungelesenen Mails erscheint an der Glocke, am Menüpunkt „Schriftverkehr“ und im
                    Mitteilungszentrum auf der Startseite. Gezählt wird jeweils der Posteingang.
                </div>
                <div className="mset-block">
                    {lesbare.map((m) => {
                        const an = gewaehlt.includes(klein(m.mailboxAddress));
                        const n = ungelesen(m.mailboxAddress);
                        const eigenes = klein(m.mailboxAddress) === klein(ownMailbox);
                        return (
                            <label
                                key={m.id}
                                className="mset-cbx"
                                style={{ display: "flex", width: "100%", padding: "6px 0" }}
                            >
                                <input type="checkbox" checked={an} onChange={() => umschalten(m.mailboxAddress)} />
                                <span className="mset-cbx-box">{an ? I.check : null}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ fontWeight: 600 }}>
                                        {m.displayName || m.mailboxAddress}
                                        {eigenes ? " (Ihr Postfach)" : ""}
                                    </span>
                                    <span className="mset-hint" style={{ display: "block" }}>
                                        {m.mailboxAddress}
                                    </span>
                                </span>
                                <span className="mset-pick-meta">
                                    {n > 0 ? `${n} ungelesen` : "keine ungelesenen"}
                                </span>
                            </label>
                        );
                    })}
                </div>
                <div className="mset-hint mset-block">
                    Standard ist nur Ihr eigenes Postfach — dann zeigt die Glocke dieselbe Zahl wie Ihr Posteingang.
                </div>
            </section>

            <section className="mset mset-card" role="group" aria-labelledby="mset-absender-titel">
                <h2 className="mset-title" id="mset-absender-titel">
                    Standard-Absender
                </h2>
                <select
                    className="mset-sel"
                    aria-labelledby="mset-absender-titel"
                    value={absenderWert}
                    onChange={(e) => absender.setze(normalisiereStandardAbsender(e.target.value))}
                >
                    <option value="">Wie bisher – das Postfach, in dem Sie gerade sind</option>
                    {sendbare.map((m) => (
                        <option key={m.id} value={m.mailboxAddress}>
                            {m.displayName ? `${m.displayName} <${m.mailboxAddress}>` : m.mailboxAddress}
                        </option>
                    ))}
                    {absenderOhneSenderecht && (
                        <option value={absenderWert}>{absenderWert} (kein Senderecht mehr)</option>
                    )}
                </select>
                <div className="mset-hint mset-block">
                    Gilt für neue Mails und für Antworten auf Mails aus Ihrem eigenen Postfach. Antworten auf Mails aus
                    einem gemeinsamen Postfach, aus dem Sie senden dürfen, gehen weiter von dort aus. Im Mailfenster
                    lässt sich der Absender wie gewohnt umstellen.
                </div>
            </section>
        </>
    );
};
